import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Car, Plus, Search, Filter, Fuel, Settings, MapPin, 
  User, Calendar, RefreshCw, Edit2, Trash2, Eye, X,
  CheckCircle, AlertCircle, Clock, Ban, ChevronDown, Zap
} from 'lucide-react';
import api from '../../services/api';

const FleetList = () => {
  const navigate = useNavigate();
  const [vehicles, setVehicles] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingVehicle, setEditingVehicle] = useState(null);
  const [drivers, setDrivers] = useState([]);
  
  // Filters
  const [filters, setFilters] = useState({
    status: '',
    fuel_type: '',
    seats: ''
  });
  const [showFilters, setShowFilters] = useState(false);

  // Form state
  const [formData, setFormData] = useState({
    brand: '',
    model: '',
    year: new Date().getFullYear(),
    plate: '',
    color: 'White',
    vehicle_category: 'sedan',
    fuel_type: 'karburant',
    transmission: 'automatic',
    seats: 4,
    status: 'active',
    assigned_driver_id: ''
  });

  useEffect(() => {
    fetchData();
  }, [filters]);

  const fetchData = async () => {
    try {
      setLoading(true);
      
      // Build query params
      const params = new URLSearchParams();
      if (filters.status) params.append('status', filters.status);
      if (filters.fuel_type) params.append('fuel_type', filters.fuel_type);
      if (filters.seats) params.append('seats', filters.seats);
      
      const [vehiclesRes, statsRes, driversRes] = await Promise.all([
        api.get(`/fleet/vehicles?${params.toString()}`),
        api.get('/fleet/vehicles/stats'),
        api.get('/drivers?limit=100')
      ]);
      
      setVehicles(vehiclesRes.data);
      setStats(statsRes.data);
      setDrivers(driversRes.data);
    } catch (error) {
      console.error('Error fetching fleet data:', error);
    } finally {
      setLoading(false);
    }
  };

  // Filtered vehicles based on search
  const filteredVehicles = useMemo(() => {
    if (!searchTerm.trim()) return vehicles;
    const term = searchTerm.toLowerCase();
    return vehicles.filter(v => 
      v.plate?.toLowerCase().includes(term) ||
      v.brand?.toLowerCase().includes(term) ||
      v.model?.toLowerCase().includes(term) ||
      v.taxi_id?.toLowerCase().includes(term) ||
      v.assigned_driver_name?.toLowerCase().includes(term)
    );
  }, [vehicles, searchTerm]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingVehicle) {
        await api.put(`/fleet/vehicles/${editingVehicle.id}`, formData);
      } else {
        await api.post('/fleet/vehicles', formData);
      }
      closeModal();
      fetchData();
    } catch (error) {
      alert('Gabim: ' + (error.response?.data?.detail || error.message));
    }
  };

  const handleDelete = async (vehicleId) => {
    if (!window.confirm('Jeni të sigurt që dëshironi të fshini këtë automjet?')) return;
    try {
      await api.delete(`/fleet/vehicles/${vehicleId}`);
      fetchData();
    } catch (error) {
      alert('Gabim gjatë fshirjes');
    }
  };

  const openAddModal = () => {
    setEditingVehicle(null);
    setFormData({
      brand: '',
      model: '',
      year: new Date().getFullYear(),
      plate: '',
      color: 'White',
      vehicle_category: 'sedan',
      fuel_type: 'karburant',
      transmission: 'automatic',
      seats: 4,
      status: 'active',
      assigned_driver_id: ''
    });
    setShowAddModal(true);
  };

  const openEditModal = (vehicle) => {
    setEditingVehicle(vehicle);
    setFormData({
      brand: vehicle.brand,
      model: vehicle.model,
      year: vehicle.year,
      plate: vehicle.plate,
      color: vehicle.color,
      vehicle_category: vehicle.vehicle_category || 'sedan',
      fuel_type: vehicle.fuel_type,
      transmission: vehicle.transmission,
      seats: vehicle.seats,
      status: vehicle.status,
      assigned_driver_id: vehicle.assigned_driver_id || ''
    });
    setShowAddModal(true);
  };

  const closeModal = () => {
    setShowAddModal(false);
    setEditingVehicle(null);
  };

  const getStatusBadge = (status) => {
    const styles = {
      'active': 'bg-green-100 text-green-700',
      'in_service': 'bg-yellow-100 text-yellow-700',
      'offline': 'bg-gray-100 text-gray-600',
      'disabled': 'bg-red-100 text-red-600'
    };
    const labels = {
      'active': 'Aktiv',
      'in_service': 'Në Servis',
      'offline': 'Offline',
      'disabled': 'Joaktiv'
    };
    const icons = {
      'active': <CheckCircle size={12} />,
      'in_service': <Settings size={12} />,
      'offline': <Clock size={12} />,
      'disabled': <Ban size={12} />
    };
    return (
      <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${styles[status] || 'bg-gray-100'}`}>
        {icons[status]}
        {labels[status] || status}
      </span>
    );
  };

  const getFuelBadge = (fuelType) => {
    const styles = {
      'karburant': 'bg-amber-100 text-amber-700',
      'elektrike': 'bg-purple-100 text-purple-700'
    };
    const labels = {
      'karburant': 'Karburant',
      'elektrike': 'Elektrike'
    };
    const icons = {
      'karburant': <Fuel size={10} />,
      'elektrike': <Zap size={10} />
    };
    return (
      <span className={`inline-flex items-center gap-1 px-2 py-1 rounded text-xs font-medium ${styles[fuelType] || 'bg-gray-100'}`}>
        {icons[fuelType] || <Fuel size={10} />}
        {labels[fuelType] || fuelType}
      </span>
    );
  };

  const getCategoryBadge = (category) => {
    const styles = {
      'sedan': 'bg-blue-100 text-blue-700',
      'suv': 'bg-green-100 text-green-700',
      'van': 'bg-orange-100 text-orange-700'
    };
    const labels = {
      'sedan': 'Sedan',
      'suv': 'SUV',
      'van': 'Van'
    };
    return (
      <span className={`inline-flex items-center gap-1 px-2 py-1 rounded text-xs font-medium ${styles[category] || 'bg-gray-100 text-gray-600'}`}>
        {labels[category] || category || 'Sedan'}
      </span>
    );
  };

  return (
    <div className="p-6">
      {/* Header */}
      <div className="mb-6">
        <div className="flex justify-between items-start mb-4">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 mb-2 flex items-center gap-3">
              <Car className="w-8 h-8 text-red-600" />
              Menaxhim Flote
            </h1>
            <p className="text-gray-600">Menaxhoni automjetet, serviset dhe dokumentet</p>
          </div>
          <button
            onClick={openAddModal}
            className="flex items-center gap-2 bg-red-600 hover:bg-red-700 text-white px-5 py-2.5 rounded-lg font-semibold transition shadow-md"
          >
            <Plus size={18} />
            Shto Automjet
          </button>
        </div>

        {/* Stats Cards */}
        {stats && (
          <div className="grid grid-cols-4 gap-4 mb-6">
            <div className="bg-white rounded-xl p-4 border shadow-sm">
              <div className="text-3xl font-bold text-gray-900">{stats.total_vehicles}</div>
              <div className="text-sm text-gray-500">Total Automjete</div>
            </div>
            <div className="bg-white rounded-xl p-4 border shadow-sm">
              <div className="text-3xl font-bold text-green-600">{stats.by_status?.active || 0}</div>
              <div className="text-sm text-gray-500">Aktivë</div>
            </div>
            <div className="bg-white rounded-xl p-4 border shadow-sm">
              <div className="text-3xl font-bold text-yellow-600">{stats.by_status?.in_service || 0}</div>
              <div className="text-sm text-gray-500">Në Servis</div>
            </div>
            <div className="bg-white rounded-xl p-4 border shadow-sm">
              <div className="text-3xl font-bold text-purple-600">{((stats.totals?.mileage || 0) / 1000).toFixed(0)}K km</div>
              <div className="text-sm text-gray-500">Total KM</div>
            </div>
          </div>
        )}

        {/* Search & Filters */}
        <div className="flex gap-4">
          <div className="flex-1 relative">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
            <input
              type="text"
              placeholder="Kërko automjet... (targë, markë, model)"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-12 pr-4 py-3 bg-white border border-gray-300 rounded-xl text-gray-900 placeholder-gray-400 focus:ring-2 focus:ring-red-500 focus:border-transparent shadow-sm"
            />
          </div>
          
          <button
            onClick={() => setShowFilters(!showFilters)}
            className={`flex items-center gap-2 px-4 py-3 rounded-xl border font-medium transition ${
              showFilters ? 'bg-red-50 border-red-200 text-red-600' : 'bg-white border-gray-300 text-gray-600'
            }`}
          >
            <Filter size={18} />
            Filtrat
            <ChevronDown size={16} className={`transition ${showFilters ? 'rotate-180' : ''}`} />
          </button>
          
          <button onClick={fetchData} className="p-3 bg-white border border-gray-300 rounded-xl text-gray-600 hover:bg-gray-50">
            <RefreshCw size={18} />
          </button>
        </div>

        {/* Filter Panel */}
        {showFilters && (
          <div className="mt-4 p-4 bg-white border border-gray-200 rounded-xl flex gap-4 flex-wrap">
            <select
              value={filters.status}
              onChange={(e) => setFilters({ ...filters, status: e.target.value })}
              className="px-3 py-2 border border-gray-300 rounded-lg text-sm"
            >
              <option value="">Të gjithë Statuset</option>
              <option value="active">Aktiv</option>
              <option value="in_service">Në Servis</option>
              <option value="offline">Offline</option>
              <option value="disabled">Joaktiv</option>
            </select>
            
            <select
              value={filters.fuel_type}
              onChange={(e) => setFilters({ ...filters, fuel_type: e.target.value })}
              className="px-3 py-2 border border-gray-300 rounded-lg text-sm"
            >
              <option value="">Të gjitha Karburantet</option>
              <option value="karburant">Karburant</option>
              <option value="elektrike">Elektrike</option>
            </select>
            
            <select
              value={filters.seats}
              onChange={(e) => setFilters({ ...filters, seats: e.target.value })}
              className="px-3 py-2 border border-gray-300 rounded-lg text-sm"
            >
              <option value="">Të gjitha Kapacitetet</option>
              <option value="4">4 Pasagjerë</option>
              <option value="6">6 Pasagjerë</option>
              <option value="8">8 Pasagjerë</option>
            </select>
            
            <button
              onClick={() => setFilters({ status: '', fuel_type: '', seats: '' })}
              className="px-3 py-2 text-red-600 hover:bg-red-50 rounded-lg text-sm font-medium"
            >
              Pastro Filtrat
            </button>
          </div>
        )}
      </div>

      {/* Table */}
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="bg-gray-50 border-b border-gray-200">
                <th className="text-left px-4 py-3 text-xs font-bold text-gray-500 uppercase tracking-wider">Taxi ID</th>
                <th className="text-left px-4 py-3 text-xs font-bold text-gray-500 uppercase tracking-wider">Automjeti</th>
                <th className="text-left px-4 py-3 text-xs font-bold text-gray-500 uppercase tracking-wider">Targa</th>
                <th className="text-center px-4 py-3 text-xs font-bold text-gray-500 uppercase tracking-wider">Kategoria</th>
                <th className="text-center px-4 py-3 text-xs font-bold text-gray-500 uppercase tracking-wider">Karburanti</th>
                <th className="text-center px-4 py-3 text-xs font-bold text-gray-500 uppercase tracking-wider">Vende</th>
                <th className="text-left px-4 py-3 text-xs font-bold text-gray-500 uppercase tracking-wider">Shoferi</th>
                <th className="text-center px-4 py-3 text-xs font-bold text-gray-500 uppercase tracking-wider">Statusi</th>
                <th className="text-right px-4 py-3 text-xs font-bold text-gray-500 uppercase tracking-wider">Udhëtime</th>
                <th className="text-center px-4 py-3 text-xs font-bold text-gray-500 uppercase tracking-wider">Veprime</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {loading ? (
                <tr>
                  <td colSpan="10" className="text-center py-8 text-gray-500">
                    <RefreshCw className="w-6 h-6 animate-spin mx-auto mb-2 text-gray-300" />
                    Duke ngarkuar...
                  </td>
                </tr>
              ) : filteredVehicles.length === 0 ? (
                <tr>
                  <td colSpan="10" className="text-center py-8 text-gray-500">
                    <Car className="w-10 h-10 mx-auto mb-2 text-gray-300" />
                    {searchTerm ? 'Nuk u gjet asnjë automjet me këtë kërkim' : 'Nuk ka automjete'}
                  </td>
                </tr>
              ) : (
                filteredVehicles.map((vehicle) => (
                  <tr 
                    key={vehicle.id} 
                    className="hover:bg-gray-50 transition-colors cursor-pointer"
                    onClick={() => navigate(`/admin/fleet/${vehicle.id}/profile`)}
                  >
                    {/* Taxi ID */}
                    <td className="px-4 py-3">
                      <span className="font-mono font-bold text-red-600 bg-red-50 px-2 py-1 rounded">
                        {vehicle.taxi_id || '—'}
                      </span>
                    </td>
                    
                    {/* Vehicle Info */}
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-lg bg-gray-100 flex items-center justify-center">
                          <Car className="w-5 h-5 text-gray-500" />
                        </div>
                        <div>
                          <div className="font-semibold text-gray-900">{vehicle.brand} {vehicle.model}</div>
                          <div className="text-xs text-gray-500">{vehicle.year} • {vehicle.color}</div>
                        </div>
                      </div>
                    </td>
                    
                    {/* Plate */}
                    <td className="px-4 py-3">
                      <span className="font-mono font-semibold text-gray-900 bg-gray-100 px-2 py-1 rounded">
                        {vehicle.plate}
                      </span>
                    </td>
                    
                    {/* Category */}
                    <td className="px-4 py-3 text-center">
                      {getCategoryBadge(vehicle.vehicle_category)}
                    </td>
                    
                    {/* Fuel Type */}
                    <td className="px-4 py-3 text-center">
                      {getFuelBadge(vehicle.fuel_type)}
                    </td>
                    
                    {/* Seats */}
                    <td className="px-4 py-3 text-center">
                      <span className="text-gray-700 font-medium">{vehicle.seats}</span>
                    </td>
                    
                    {/* Driver */}
                    <td className="px-4 py-3">
                      {vehicle.assigned_driver_name ? (
                        <div className="flex items-center gap-2">
                          <User size={14} className="text-gray-400" />
                          <span className="text-gray-700">{vehicle.assigned_driver_name}</span>
                        </div>
                      ) : (
                        <span className="text-gray-400 text-sm">Pa shofer</span>
                      )}
                    </td>
                    
                    {/* Status */}
                    <td className="px-4 py-3 text-center">
                      {getStatusBadge(vehicle.status)}
                    </td>
                    
                    {/* Rides */}
                    <td className="px-4 py-3 text-right">
                      <span className="font-semibold text-gray-900">{vehicle.total_rides}</span>
                    </td>
                    
                    {/* Actions */}
                    <td className="px-4 py-3" onClick={(e) => e.stopPropagation()}>
                      <div className="flex items-center justify-center gap-1">
                        <button
                          onClick={() => navigate(`/admin/fleet/${vehicle.id}/profile`)}
                          className="p-1.5 bg-blue-500 hover:bg-blue-600 text-white rounded transition"
                          title="Shiko Detajet"
                        >
                          <Eye size={14} />
                        </button>
                        <button
                          onClick={() => openEditModal(vehicle)}
                          className="p-1.5 bg-gray-500 hover:bg-gray-600 text-white rounded transition"
                          title="Ndrysho"
                        >
                          <Edit2 size={14} />
                        </button>
                        <button
                          onClick={() => handleDelete(vehicle.id)}
                          className="p-1.5 bg-red-500 hover:bg-red-600 text-white rounded transition"
                          title="Fshi"
                        >
                          <Trash2 size={14} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Footer */}
        {!loading && filteredVehicles.length > 0 && (
          <div className="px-6 py-4 bg-gray-50 border-t border-gray-200 flex justify-between items-center">
            <span className="text-sm text-gray-500">
              Duke shfaqur {filteredVehicles.length} nga {vehicles.length} automjete
            </span>
            <button onClick={fetchData} className="flex items-center gap-2 text-sm text-gray-600 hover:text-gray-900">
              <RefreshCw size={14} />
              Rifresko
            </button>
          </div>
        )}
      </div>

      {/* Add/Edit Modal */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center p-5 border-b border-gray-200 sticky top-0 bg-white">
              <h2 className="text-xl font-bold text-gray-900">
                {editingVehicle ? 'Ndrysho Automjetin' : 'Shto Automjet të Ri'}
              </h2>
              <button onClick={closeModal} className="p-2 hover:bg-gray-100 rounded-full">
                <X size={20} />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="p-5 space-y-4">
              {/* Brand & Model */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Marka *</label>
                  <input
                    type="text"
                    value={formData.brand}
                    onChange={(e) => setFormData({ ...formData, brand: e.target.value })}
                    placeholder="Toyota, Mercedes, VW..."
                    className="w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Modeli *</label>
                  <input
                    type="text"
                    value={formData.model}
                    onChange={(e) => setFormData({ ...formData, model: e.target.value })}
                    placeholder="Corolla, E-Class..."
                    className="w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500"
                    required
                  />
                </div>
              </div>

              {/* Year & Plate */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Viti *</label>
                  <input
                    type="number"
                    value={formData.year}
                    onChange={(e) => setFormData({ ...formData, year: parseInt(e.target.value) })}
                    min="2000"
                    max="2030"
                    className="w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Targa *</label>
                  <input
                    type="text"
                    value={formData.plate}
                    onChange={(e) => setFormData({ ...formData, plate: e.target.value.toUpperCase() })}
                    placeholder="AA 123 BB"
                    className="w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 font-mono"
                    required
                  />
                </div>
              </div>

              {/* Color & Seats */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Ngjyra</label>
                  <select
                    value={formData.color}
                    onChange={(e) => setFormData({ ...formData, color: e.target.value })}
                    className="w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500"
                  >
                    <option value="White">E Bardhë</option>
                    <option value="Black">E Zezë</option>
                    <option value="Silver">Argjend</option>
                    <option value="Gray">Gri</option>
                    <option value="Blue">Blu</option>
                    <option value="Red">E Kuqe</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Vende</label>
                  <select
                    value={formData.seats}
                    onChange={(e) => setFormData({ ...formData, seats: parseInt(e.target.value) })}
                    className="w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500"
                  >
                    <option value={4}>4 Pasagjerë</option>
                    <option value={5}>5 Pasagjerë</option>
                    <option value={6}>6 Pasagjerë</option>
                    <option value={7}>7 Pasagjerë</option>
                    <option value={8}>8 Pasagjerë</option>
                  </select>
                </div>
              </div>

              {/* Fuel & Transmission */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Karburanti</label>
                  <select
                    value={formData.fuel_type}
                    onChange={(e) => setFormData({ ...formData, fuel_type: e.target.value })}
                    className="w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500"
                  >
                    <option value="karburant">Karburant</option>
                    <option value="elektrike">Elektrike</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Transmisioni</label>
                  <select
                    value={formData.transmission}
                    onChange={(e) => setFormData({ ...formData, transmission: e.target.value })}
                    className="w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500"
                  >
                    <option value="automatic">Automatik</option>
                    <option value="manual">Manual</option>
                  </select>
                </div>
              </div>

              {/* Category */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Kategoria e Automjetit</label>
                <div className="grid grid-cols-3 gap-3">
                  <button
                    type="button"
                    onClick={() => setFormData({ ...formData, vehicle_category: 'sedan' })}
                    className={`py-3 rounded-lg border-2 font-medium transition ${
                      formData.vehicle_category === 'sedan'
                        ? 'border-blue-500 bg-blue-50 text-blue-700'
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    Sedan
                  </button>
                  <button
                    type="button"
                    onClick={() => setFormData({ ...formData, vehicle_category: 'suv' })}
                    className={`py-3 rounded-lg border-2 font-medium transition ${
                      formData.vehicle_category === 'suv'
                        ? 'border-green-500 bg-green-50 text-green-700'
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    SUV
                  </button>
                  <button
                    type="button"
                    onClick={() => setFormData({ ...formData, vehicle_category: 'van' })}
                    className={`py-3 rounded-lg border-2 font-medium transition ${
                      formData.vehicle_category === 'van'
                        ? 'border-orange-500 bg-orange-50 text-orange-700'
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    Van
                  </button>
                </div>
              </div>

              {/* Status & Driver */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Statusi</label>
                  <select
                    value={formData.status}
                    onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                    className="w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500"
                  >
                    <option value="active">Aktiv</option>
                    <option value="in_service">Në Servis</option>
                    <option value="offline">Offline</option>
                    <option value="disabled">Joaktiv</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Shoferi</label>
                  <select
                    value={formData.assigned_driver_id}
                    onChange={(e) => setFormData({ ...formData, assigned_driver_id: e.target.value })}
                    className="w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500"
                  >
                    <option value="">Pa shofer</option>
                    {drivers.map(d => (
                      <option key={d.id} value={d.id}>{d.name}</option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Buttons */}
              <div className="flex gap-3 pt-4">
                <button
                  type="submit"
                  className="flex-1 bg-red-600 hover:bg-red-700 text-white font-semibold py-2.5 rounded-lg transition"
                >
                  {editingVehicle ? 'Ruaj Ndryshimet' : 'Shto Automjetin'}
                </button>
                <button
                  type="button"
                  onClick={closeModal}
                  className="px-5 bg-gray-100 hover:bg-gray-200 text-gray-700 font-semibold py-2.5 rounded-lg transition"
                >
                  Anulo
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default FleetList;
